/* eslint-disable @typescript-eslint/ban-ts-comment */
import Chevron from "../../Icons/Chevron";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Button from "../../Shared/Button";
import { ChaptersDTO, getChapters } from "../../../api/Chapters/GetAllChapters";
import Input from "../../Shared/Input";
import { ButtonTypes } from "../../../utils/consts";
import ChooseLecture from "./ChooseLecture";
import TextArea from "../../Shared/TextArea";
import Spinner from "../../Shared/Spinner";
import MultipleUpload from "../../Shared/MultipleUpload";
import { toast } from "react-toastify";
import Upload from "../../Shared/Upload";
import { getCourses } from "../../../api/Courses/GetAllCourses";
import Selectbox, { option } from "../../Shared/SelectBox";
import { CreateBankQuestion } from "../../../api/QuestionBank/CreateQuestion";
import { queryClient } from "../../../main";
interface Choice {
    answer: string;
    isCorrect: boolean;
}

interface Question {
    id: number;
    name: string;
    choices: Choice[];
    correctAnswerIndex: number;
    explanation: string;
    figure: string[];
    attachment: string;
}

const AddBankQuestions = ({ onSuccess }: { onSuccess?: () => void }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [selected, setSelected] = useState<ChaptersDTO | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<option | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [, setErrors] = useState({
        title: '',
        questions: [] as string[],
    });

    const [mcqQuestions, setMcqQuestions] = useState<Question[]>([
        {
            id: 1,
            name: "",
            choices: [
                { answer: "", isCorrect: true },
                { answer: "", isCorrect: false },
                { answer: "", isCorrect: false },
                { answer: "", isCorrect: false },
            ],
            correctAnswerIndex: 0,
            explanation: "",
            attachment: "",
            figure: [],
        },
    ]);


    const [nextMcqId, setNextMcqId] = useState(2);

    const validateForm = () => {
        let hasError = false;
        const questionErrors: string[] = [];

        mcqQuestions.forEach((question, index) => {
            if (!question.name.trim() || question.choices.some(choice => !choice.answer.trim())) {
                questionErrors[index] = "All questions and choices are required.";
                hasError = true;
            } else {
                questionErrors[index] = "";
            }
        });

        setErrors(prev => ({ ...prev, questions: questionErrors }));
        return !hasError;
    };

    const chapterId = selectedCourse?.id || "";
    const { data: ChaptersResponse, isLoading: isChaptersLoading } = useQuery({
        queryFn: () => getChapters(chapterId),
        queryKey: ["chapters", chapterId],
        staleTime: 0,
        refetchInterval: 0,
    });
    const { data: CoursesResponse, } = useQuery({
        queryFn: getCourses,
        queryKey: ["courses"],
        staleTime: 0,
        refetchInterval: 0,
    });

    const courses = CoursesResponse?.data?.coursesData || [];

    const courseOptions = courses.map((course) => ({
        id: course.id,
        name: course.name,
    }));

    const chapters = (ChaptersResponse?.data as ChaptersDTO[] | undefined) || [];

    const handleAddQuestion = () => {
        const newQuestion: Question = {
            id: nextMcqId,
            name: "",
            choices: [
                { answer: "", isCorrect: true },
                { answer: "", isCorrect: false },
                { answer: "", isCorrect: false },
                { answer: "", isCorrect: false },
            ],
            correctAnswerIndex: 0,
            explanation: "",
            attachment: "",
            figure: [],
        };
        setMcqQuestions([...mcqQuestions, newQuestion]);
        setNextMcqId(nextMcqId + 1);
    };

    const handleDeleteQuestion = (questionId: number) => {
        setMcqQuestions(mcqQuestions.filter((question) => question.id !== questionId));
    };

    const handleQuestionFieldChange = (
        questionIndex: number,
        field: string,
        value: string | boolean,
        choiceIndex?: number,
    ) => {
        setMcqQuestions((prevQuestions) =>
            prevQuestions.map((question, index) => {
                if (index === questionIndex) {
                    if (typeof choiceIndex !== "undefined") {
                        const updatedChoices = question.choices.map((choice, idx) =>
                            idx === choiceIndex ? { ...choice, [field]: value } : choice
                        );
                        return { ...question, choices: updatedChoices };
                    } else {
                        return { ...question, [field]: value };
                    }
                }
                return question;
            })
        );
    };

    const handleCorrectAnswerChange = (
        questionIndex: number,
        selectedChoiceIndex: number,
    ) => {
        setMcqQuestions((prevQuestions) =>
            prevQuestions.map((question, index) => {
                if (index === questionIndex) {
                    const updatedChoices = question.choices.map((choice, idx) => ({
                        ...choice,
                        isCorrect: idx === selectedChoiceIndex,
                    }));
                    return {
                        ...question,
                        choices: updatedChoices,
                        correctAnswerIndex: selectedChoiceIndex,
                    };
                }
                return question;
            })
        );
    };

    const { mutate } = useMutation({
        //@ts-ignore
        mutationFn: CreateBankQuestion,
        onSuccess: () => {
            setIsSubmitting(true); // Disable the submit button
            queryClient.invalidateQueries({ queryKey: ["bank"] });
            onSuccess && onSuccess();
            toast.success("Quiz Added successfully");
        },
        onError: () => {
            setIsSubmitting(false);
        },
    });

    const handleSubmit = (event: { preventDefault: () => void }) => {
        event.preventDefault();
    
        if (validateForm()) {
            const payload = mcqQuestions.map(question => ({
                question: question.name,
                explanation: question.explanation,
                attachment: question.attachment,
                courseId: selectedCourse?.id,  // Add courseId to each question
                chapterId: selected?.chapterId, // Add chapterId to each question
                lectureId: selected?.lectureId, // Add lectureId to each question
                figure: question.figure,
                choices: question.choices.map(choice => ({
                    answer: choice.answer,
                    isCorrect: choice.isCorrect,
                }))
            }));
                setIsSubmitting(true);
            //@ts-ignore
            mutate(payload);
        }
    };
    
    const handleFileUploaded = (url: string, questionId: number) => {
        setMcqQuestions((prevQuestions) => {
            const updatedQuestions = prevQuestions.map((question) =>
                question.id === questionId
                    ? { ...question, figure: [...question.figure, url] }
                    : question
            );
            return updatedQuestions;
        });
    };

    const handleAttachmentUpload = (url: string, questionId: number) => {
        setMcqQuestions((prevQuestions) => {
            const updatedQuestions = prevQuestions.map((question) =>
                question.id === questionId
                    ? { ...question, attachment: url }
                    : question
            );
            return updatedQuestions;
        });
    };
    const handleSaveAndContinue = () => {
        if (!selectedCourse) {
            setCurrentStep(3);
        } else {
            setCurrentStep(2);
        }
    };
    return (
        <div className="py-7">
            <h1 className="text-burntBlack font-bold text-[32px] mb-6">
                Add questions to the bank
            </h1>
            <form
                onSubmit={handleSubmit}
                className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5"
            >
                <div className="flex gap-5 items-center mb-9 mt-4 justify-center">
                    <div className={`flex items-center gap-2.5 `}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 1 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            1
                        </span>
                        <h1 className="font-semibold">Choose Course</h1>
                    </div>
                    <Chevron height={20} width={20} />
                    <div className={`flex items-center gap-2.5`}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 2 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            2
                        </span>
                        <h1 className="font-semibold">Choose Lecture</h1>
                    </div>
                    <Chevron height={20} width={20} />

                    <div className={`flex items-center gap-2.5 `}>
                        <span
                            className={`tranistion ease-in-out duration-300 w-9 h-9 rounded-full border border-black/15 font-bold text-xl flex items-center justify-center ${currentStep === 3 ? "text-white bg-[#4379EE]" : "text-lightGray"
                                }`}
                        >
                            3
                        </span>
                        <h1 className="font-semibold">Questions</h1>
                    </div>
                </div>
                {currentStep === 1 && (
                    <>
                        {isChaptersLoading ? (
                            <div className="min-h-[50vh] flex justify-center items-center">
                                <Spinner />
                            </div>
                        ) : (
                            <div>
                                <Selectbox
                                    title="Select Course"
                                    name="course"
                                    placeholder="Select a course"
                                    selectOptions={courseOptions}
                                    selected={selectedCourse}
                                    setSelected={setSelectedCourse}
                                />

                                <div className="flex justify-between mt-20">
                                    <Button
                                        onClick={() => onSuccess && onSuccess()}
                                        className={`!bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold `}
                                        variant="primary"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSaveAndContinue}
                                        className={`mt-8 w-[160px] !h-11 !text-base font-bold ml-auto`}
                                        variant="primary"
                                    >
                                        Save & Continue
                                    </Button>
                                </div>

                            </div>
                        )}
                    </>
                )}

                {currentStep === 2 && (
                    <>
                        {isChaptersLoading ? (
                            <div className="min-h-[50vh] flex justify-center items-center">
                                <Spinner />
                            </div>
                        ) : (
                            <ChooseLecture
                                message="Select a lesson to make quizzes of it"
                                currentStep={currentStep}
                                setCurrentStep={setCurrentStep}
                                selected={selected}
                                setSelected={setSelected}
                                data={chapters}
                            />
                        )}
                    </>
                )}
                {currentStep === 3 && (
                    <>
                        <div className="w-full max-w-[544px] mx-auto">
                            <div>
                                <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                                <p className="text-lightGray font-medium">
                                    Nice text goes here to explain the question creation part like
                                    never before
                                </p>
                            </div>
                            <div>
                                {mcqQuestions.map((question, questionIndex) => (
                                    <div key={question.id}>
                                        <div className="mt-9">
                                            <Input
                                                className="text-lg font-semibold"
                                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                                title="Question name"
                                                placeholder=""
                                                name="name"
                                                value={question.name}
                                                onChange={(e) =>
                                                    handleQuestionFieldChange(
                                                        questionIndex,
                                                        "name",
                                                        e.target.value,
                                                        undefined
                                                    )
                                                }
                                                type="text"
                                            />
                                            <p className="text-lightGray font-medium mb-4">
                                                Please mark the correct answer among them
                                            </p>
                                            <div className="grid-cols-2 grid gap-x-6 gap-y-4">
                                                {question.choices.map((choice, choiceIndex) => (
                                                    <div key={`choice-${choiceIndex}`}>
                                                        <div className="flex items-center justify-between">
                                                            <label
                                                                htmlFor={`choice-radio-${questionIndex}-${choiceIndex}`}
                                                                className="text-lg font-semibold"
                                                            >
                                                                {`Choice ${choiceIndex + 1}`}
                                                            </label>
                                                            <input
                                                                id={`choice-radio-${questionIndex}-${choiceIndex}`}
                                                                type="radio"
                                                                value={choiceIndex}
                                                                name={`question-${question.id}-choices`}
                                                                checked={
                                                                    choiceIndex === question.correctAnswerIndex
                                                                }
                                                                onChange={() =>
                                                                    handleCorrectAnswerChange(
                                                                        questionIndex,
                                                                        choiceIndex
                                                                    )
                                                                }
                                                                className="w-5 h-5 text-primary bg-gray-100 border-black/30 border-2 focus:ring-0"
                                                            />
                                                        </div>
                                                        <Input
                                                            className="text-lg font-semibold"
                                                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                                            placeholder=""
                                                            name={`choice-${choiceIndex}`}
                                                            value={choice.answer}
                                                            onChange={(e) =>
                                                                handleQuestionFieldChange(
                                                                    questionIndex,
                                                                    "answer",
                                                                    e.target.value,
                                                                    choiceIndex,
                                                                )
                                                            }
                                                            type="text"
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            <TextArea
                                                className="text-lg font-semibold mb-10"
                                                inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                                                title="Explanation (Optional)"
                                                placeholder=""
                                                name="explanation"
                                                value={question.explanation}
                                                onChange={(e) =>
                                                    handleQuestionFieldChange(
                                                        questionIndex,
                                                        "explanation",
                                                        e.target.value,
                                                        undefined
                                                    )
                                                }
                                            />
                                            <MultipleUpload
                                                folder="courses_images"
                                                onFileUploaded={(url) =>
                                                    handleFileUploaded(url, question.id)
                                                }
                                                questionId={question.id}
                                            >
                                                <Input
                                                    upload={true}
                                                    className="text-lg font-semibold mb-10"
                                                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                                    placeholder=""
                                                    title="Figures"
                                                    name="img_url"
                                                    type="text"
                                                />
                                            </MultipleUpload>
                                            <Upload
                                                acceptedFiles={{ "pdf": [] }}
                                                folder="quiz_resources"
                                                onFileUploaded={(url) => handleAttachmentUpload(url, question.id)}

                                            >
                                                <Input
                                                    upload={true}
                                                    className="text-lg font-semibold"
                                                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                                                    placeholder=""
                                                    title="Attachments"
                                                    name="attachment"
                                                    type="text"
                                                />
                                            </Upload>

                                        </div>
                                        <div className="flex justify-end gap-5 mt-10">
                                            <Button
                                                onClick={() => handleDeleteQuestion(question.id)}
                                                className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary ${mcqQuestions.length === 1
                                                    ? "opacity-50 cursor-not-allowed"
                                                    : ""
                                                    }`}
                                                disabled={mcqQuestions.length === 1}
                                                type={ButtonTypes.BUTTON}
                                                variant="transparent"
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                onClick={() => handleAddQuestion()}
                                                type={ButtonTypes.BUTTON}
                                                variant="primary"
                                                className="!h-12 w-40 !text-base !font-bold "
                                            >
                                                Add New Question
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-between w-full">
                            <Button
                                onClick={() => setCurrentStep(2)}
                                className={`mt-8 !bg-white border-black/20 border !text-burntBlack w-[160px] !h-11 !text-base font-bold `}
                                variant="primary"
                            >
                                Cancel
                            </Button>
                            <Button
                                type={ButtonTypes.SUBMIT}
                                disabled={isSubmitting}
                                className={`mt-8 w-[160px] !h-11 !text-base font-bold`}
                                variant="primary"
                            >
                                Send Replies
                            </Button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default AddBankQuestions;
